import { ChangeDetectionStrategy, Component, booleanAttribute, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FuseConfigService } from '@fuse/services/config';
import { TranslocoService } from '@jsverse/transloco';
import { map } from 'rxjs';

/** Idiomas que têm arte da tagline. Fora deles, a logo cai na variante sem tagline. */
const LANGUAGES_WITH_ARTWORK = ['pt', 'en', 'es'];

/**
 * A logo da marca, resolvendo idioma e modo claro/escuro sozinha.
 *
 * A arte existe em três idiomas porque a tagline é traduzida (`CARDÁPIO DIGITAL`, `DIGITAL MENU`,
 * `MENÚ DIGITAL`) e em duas variantes de fundo. São doze arquivos, e repetir a escolha em cada tela
 * garantiria divergência: hoje já há `@if (!onDark)` copiado em quatro componentes de auth.
 *
 * 🔴 Idioma desconhecido cai na arte SEM tagline, não num idioma chutado. O `availableLangs` é
 * `pt`/`en`/`es`, mas `setActiveLang` aceita qualquer string: um valor fora do conjunto pediria um
 * arquivo que não existe e a tela ficaria sem logo nenhuma. A arte neutra é sempre válida.
 *
 * ⚠️ `scheme` do Fuse é `'auto' | 'dark' | 'light'`, e em `'auto'` quem decide é a media query do
 * sistema. Os componentes de auth comparam `scheme === 'dark'` direto e, nesse modo, servem a arte
 * clara sobre fundo escuro — este componente resolve o `'auto'`.
 */
@Component({
  selector: 'hmax-logo',
  standalone: true,
  templateUrl: './logo.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class LogoComponent {
  private readonly _transloco = inject(TranslocoService);
  private readonly _fuseConfig = inject(FuseConfigService);

  /** Sem tagline: a única variante neutra de idioma. Use onde a tagline não seria legível. */
  readonly tagline = input(true, { transform: booleanAttribute });

  /** Força a variante escura, para painel decorativo escuro dentro de tela clara. */
  readonly onDark = input<boolean | undefined>(undefined);

  private readonly _activeLanguage = toSignal(this._transloco.langChanges$, {
    initialValue: this._transloco.getActiveLang(),
  });

  // `config` do Fuse é setter SEM getter: `this._fuseConfig.config?.scheme` compila (o tipo é
  // `any`) e devolve `undefined` em runtime. O valor real vem do `config$`, que é um
  // `BehaviorSubject` e emite na assinatura — então o `initialValue` nunca chega à tela.
  private readonly _scheme = toSignal(this._fuseConfig.config$.pipe(map(({ scheme }) => scheme)), {
    initialValue: undefined,
  });

  private readonly _isOnDark = computed(() => {
    const forced = this.onDark();

    if (forced !== undefined) {
      return forced;
    }

    const scheme = this._scheme();

    if (scheme === 'auto') {
      return globalThis.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
    }

    return scheme === 'dark';
  });

  private readonly _fileName = computed(() => {
    const language = this._activeLanguage();
    const suffix = this.tagline() && LANGUAGES_WITH_ARTWORK.includes(language) ? `-${language}` : '';

    return `logo${suffix}${this._isOnDark() ? '-on-dark' : ''}`;
  });

  readonly png = computed(() => `images/logo/${this._fileName()}.png`);
  readonly webp = computed(() => `images/logo/${this._fileName()}.webp`);
}
